import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import Heading from './heading';
import Text from './text';

// ─── SettingsPage ─────────────────────────────────────────────────────────────

export type SettingsPageProps = {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
};

function SettingsPage({
  title,
  description,
  children,
  className,
}: SettingsPageProps) {
  return (
    <div className={cn('flex flex-col gap-7 sm:gap-6', className)}>
      <div className="flex flex-col gap-3 sm:gap-2">
        <Heading level={1}>{title}</Heading>
        {description && <Text variant="muted">{description}</Text>}
      </div>
      <div className="flex flex-col gap-10 sm:gap-8">{children}</div>
    </div>
  );
}

// ─── SettingsSection ──────────────────────────────────────────────────────────

export type SettingsSectionProps = {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
};

function SettingsSection({
  title,
  description,
  children,
  className,
}: SettingsSectionProps) {
  return (
    <div className={cn('flex flex-col gap-5 sm:gap-4', className)}>
      <div className="flex flex-col gap-3 sm:gap-2">
        <Text size="lg" weight="medium">
          {title}
        </Text>
        {description && (
          <Text variant="muted" size="sm">
            {description}
          </Text>
        )}
      </div>
      <div className="flex flex-col gap-5 sm:gap-4">{children}</div>
    </div>
  );
}

// ─── SettingItem ──────────────────────────────────────────────────────────────

export type SettingItemProps = {
  label: string;
  children: ReactNode;
  helpText?: string;
  layout?: 'horizontal' | 'vertical';
  className?: string;
};

function SettingItem({
  label,
  children,
  helpText,
  layout = 'horizontal',
  className,
}: SettingItemProps) {
  if (layout === 'vertical') {
    return (
      <div className={cn('space-y-2', className)}>
        <div>
          <Text size="sm" weight="medium">
            {label}
          </Text>
          {helpText && (
            <Text size="xs" variant="muted" className="mt-1">
              {helpText}
            </Text>
          )}
        </div>
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'hover:bg-base-200/50 flex items-center justify-between rounded-lg px-3 py-2 transition-colors',
        className
      )}
    >
      <div className="mr-4 flex-1">
        <Text size="sm">{label}</Text>
        {helpText && (
          <Text size="xs" variant="muted" className="mt-1">
            {helpText}
          </Text>
        )}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export { SettingsPage, SettingsSection, SettingItem };
